import { CalendarDate } from "@sp-crm/core";
import * as React from "react";
import { DatePicker } from "./date-time-picker/date-picker";

interface CalendarDateProps {
    className?: string;
    label: string;
    textSize?: "sm";
    initial?: CalendarDate | undefined | null;
    value?: CalendarDate | undefined | null;
    minDate?: CalendarDate | null;
    maxDate?: CalendarDate | null;
    required?: boolean;
    onChange?: (newValue: CalendarDate | null) => void;
    onCommit?: (newValue: CalendarDate | null) => void;
}

const toCalendarDate = (iso: string | null): CalendarDate | null =>
    iso ? CalendarDate.parse(iso).getOrElse(CalendarDate.future) : null;

const toIso = (value: CalendarDate | null): string | null =>
    value ? value.toString() : null;

export const CalendarDateInput: React.FC<CalendarDateProps> = props => {
    const {
        label,
        textSize,
        initial,
        value,
        minDate,
        maxDate,
        required,
        onChange,
        onCommit,
    } = props;

    const [current, setCurrent] = React.useState<CalendarDate | null>(
        value ?? initial ?? null,
    );
    const currentRef = React.useRef<CalendarDate | null>(value ?? initial ?? null);
    const lastCommitRef = React.useRef<CalendarDate | null>(value ?? initial ?? null);
    const isOpenRef = React.useRef<boolean>(false);

    React.useEffect(() => {
        if (value !== undefined) {
            currentRef.current = value;
            setCurrent(value);
        }
    }, [value]);

    const commit = (next: CalendarDate | null): void => {
        if (!onCommit) {
            return;
        }
        if (toIso(lastCommitRef.current) !== toIso(next)) {
            lastCommitRef.current = next;
            onCommit(next);
        }
    };

    const handleChange = (iso: string | null): void => {
        const next = toCalendarDate(iso);
        currentRef.current = next;
        setCurrent(next);
        if (onChange) {
            onChange(next);
        }
        if (next === null || isOpenRef.current) {
            commit(next);
        }
    };

    const handleBlur = (event: React.FocusEvent<HTMLDivElement>): void => {
        if (event.currentTarget.contains(event.relatedTarget)) {
            return;
        }
        commit(currentRef.current);
    };

    return (
        <div className="w-full" onBlur={handleBlur}>
            <DatePicker
                label={label}
                value={toIso(current)}
                onChange={handleChange}
                onOpenChange={isOpen => {
                    isOpenRef.current = isOpen;
                }}
                disabled={false}
                required={required ?? false}
                textSize={textSize === "sm" ? "sm" : "base"}
                minValue={toIso(minDate ?? null)}
                maxValue={toIso(maxDate ?? null)}
                visibleYears={200}
            />
        </div>
    );
};
